import { Box, Card, CardContent, Divider, Stack, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useDataProvider } from "react-admin";
import { GroupSiteAssociationFormValues, SelectedGroup, SelectedSite } from "../../types";

import Chip from "@mui/material/Chip";
import { format } from "date-fns";
import { prepareAssoDataForSummary } from "../helpers";
import { fetchSelectedGroupsQuery, fetchSelectedSitesQuery } from "../queries";

type AssociationOperationsSummaryProps = {
  data: Partial<GroupSiteAssociationFormValues> | null;
};

type PreparedAssociationSummary = GroupSiteAssociationFormValues & { sites: SelectedSite[]; groups: SelectedGroup[] };

function formatDate(iso: string): string {
  return format(new Date(iso), "dd/MM/yyyy");
}

function SiteList({ sites }: { sites: SelectedSite[] | undefined }) {
  if (!sites) return null;
  return (
    <Stack direction="row" flexWrap="wrap" gap={0.75}>
      {sites.map((site) => (
        <Chip key={site.id} label={`${site.id} - ${site.name}`} size="small" variant="outlined" sx={{ fontSize: 12 }} />
      ))}
    </Stack>
  );
}

function GroupList({ groups }: { groups: SelectedGroup[] | undefined }) {
  if (!groups) return null;
  return (
    <Stack direction="row" flexWrap="wrap" gap={0.75}>
      {groups.map((group) => (
        <Chip key={group.id} label={group.name} size="small" variant="outlined" sx={{ fontSize: 12 }} />
      ))}
    </Stack>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Stack direction="row" alignItems="flex-start" gap={2}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60, fontSize: 12, pt: 0.4 }}>
        {label}
      </Typography>
      {children}
    </Stack>
  );
}

export default function AssociationOperationsSummary({ data }: AssociationOperationsSummaryProps) {
  const dataprovider = useDataProvider();
  const normalizedData = useMemo(() => {
    if (!data) return null;
    return prepareAssoDataForSummary(data as GroupSiteAssociationFormValues);
  }, [data]);

  const [preparedData, setPreparedData] = useState<PreparedAssociationSummary | null>(null);

  useEffect(() => {
    let isMounted = true;
    const loadSummaryData = async () => {
      if (!normalizedData) {
        if (isMounted) setPreparedData(null);
        return;
      }

      const [sites, groups] = await Promise.all([
        fetchSelectedSitesQuery(dataprovider, normalizedData.site_ids as number[]),
        fetchSelectedGroupsQuery(dataprovider, normalizedData.group_ids ?? []),
      ]);

      if (!isMounted) return;
      setPreparedData({ ...normalizedData, sites, groups });
    };
    void loadSummaryData();
    return () => {
      isMounted = false;
    };
  }, [dataprovider, normalizedData]);

  if (!preparedData) return null;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        You are about to create the following associations:
      </Typography>

      <Card variant="outlined">
        <CardContent>
          <Stack gap={1.5}>
            <Row label="Groups">
              <GroupList groups={preparedData.groups} />
            </Row>

            <Row label="Sites">
              <SiteList sites={preparedData.sites} />
            </Row>

            <Divider />

            <Row label="From">
              <Typography variant="body2" fontWeight={500}>
                {formatDate(preparedData.start_date as string)}
              </Typography>
            </Row>

            <Row label="To">
              {preparedData.end_date ? (
                <Typography variant="body2" fontWeight={500}>
                  {formatDate(preparedData.end_date as string)}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.disabled" sx={{ fontStyle: "italic" }}>
                  no end date
                </Typography>
              )}
            </Row>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
